/**
 * The brief a follow-up resource is generated from: what one pupil got wrong
 * on a quiz, written as notes for the composer's source box.
 *
 * It goes to the general model provider as ordinary context, so it never
 * carries the pupil's name – they are "the student" throughout (the same rule
 * as utils/homeworkQuiz.js). Kept as pure functions with no DOM access so the
 * wording can be tested directly.
 */
(function (root) {
    'use strict';

    // The quiz and worksheet routes keep the first 12,000 characters of
    // context (MAX_CONTEXT_CHARS in utils/lessonPack.js). Stopping short of
    // that here means the cut falls between questions rather than through one.
    const MAX_CHARS = 10000;

    const oneLine = (value) => String(value ?? '').replace(/\s+/g, ' ').trim();

    function entry(item, n) {
        const lines = [`${n}. ${oneLine(item.question) || 'Question'}`];
        // A whiteboard answer is a drawing: there is nothing to quote, and the
        // picture itself stays where it is.
        if (item.type !== 'whiteboard' && oneLine(item.given)) {
            lines.push(`   The student answered: ${oneLine(item.given)}`);
        }
        if (oneLine(item.correct)) lines.push(`   Correct answer: ${oneLine(item.correct)}`);
        return lines.join('\n');
    }

    /**
     * brief(missed, { title }) -> { context, count }
     *
     * `missed` is [{ type, question, given, correct }], already as the text a
     * teacher would read. `count` is how many of them fitted.
     */
    function brief(missed, { title = '' } = {}) {
        const items = Array.isArray(missed) ? missed : [];
        if (!items.length) return { context: '', count: 0 };

        const n = items.length;
        const quiz = oneLine(title) ? `the quiz "${oneLine(title)}"` : 'a quiz';
        const head = [
            `Follow-up for a student who sat ${quiz} and got ${n} question${n === 1 ? '' : 's'} wrong.`,
            'Build this around the ideas behind those questions: re-teach them, then give fresh practice.'
                + ' Do not reuse the questions below word for word.',
        ].join('\n');

        const entries = [];
        let length = head.length;
        for (const item of items) {
            const text = entry(item, entries.length + 1);
            if (entries.length && length + text.length + 2 > MAX_CHARS) break;
            entries.push(text);
            length += text.length + 2;
        }

        return { context: [head, ...entries].join('\n\n').slice(0, MAX_CHARS), count: entries.length };
    }

    const api = { brief, MAX_CHARS };

    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    else root.QuizFollowUp = api;
}(typeof globalThis !== 'undefined' ? globalThis : this));
